import { createSelector } from 'reselect';
import { parse } from 'qs';
import { utils } from '@saucelabs/sl-module-performance';
import { records as userRecords } from '@saucelabs/sl-module-user';

import { filterCommands, filterEspressoTests, filterTestResult, processCrashLogs } from './utils';
import * as jobSelectors from './../job/selectors';

const job = (store) => store.apps.job;
const performance = (store) => store.apps.performance;
const location = (store) => store.router.location;
const jobContainer = (store) => store.apps.job.container;
const jobSelector = (store) => store.apps.job.job;

const containerSelector = createSelector([
  jobContainer,
  jobSelector,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
],
(filteredContainer, filteredJob, user) => ({
  applicationSummary: filteredJob.get('job').application_summary,
  build: filteredJob.get('build'),
  runningState: filteredJob.get('runningState'),
  loading: filteredContainer.get('loading'),
  notifications: filteredContainer.get('notifications'),
  openSauceUser: filteredContainer.get('openSauceUser'),
  commands: filteredJob.get('commands'),
  isJobOwner: user.username === filteredJob.getIn(['job', 'owner']),
  job: filteredJob.get('job'),
  user,
  logsViewerState: filteredContainer.get('logsViewerState'),
}));

const PuppeteerReplayContainerSelector = createSelector([
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  location,
  jobSelectors.selectActiveCommand,
  jobSelectors.selectScreenshotBundleUrl,
],
(filteredState, user, routerLocation, activeCommand, screenshotBundleUrl) => ({
  activeCommand,
  assets: filteredState.job.get('assets'),
  auth: parse(routerLocation.search, { ignoreQueryPrefix: true }).auth,
  build: filteredState.job.get('build'),
  columnsRatio: filteredState.container.get('ratio'),
  commandCount: filteredState.job.get('commands')?.size || 0,
  commandsLoadStatus: filteredState.job.get('commandsLoadStatus'),
  files: filteredState.files.get('files'),
  filteredCommands: filterCommands({
    commands: filteredState.job.get('commands'),
    filterState: filteredState.container.get('commandsFilter'),
  }),
  isOpenSauceUser: !user.isAuthenticated(),
  isVideoReady: filteredState.job.get('assetListFetched'),
  job: filteredState.job.get('job'),
  logFetchStatus: filteredState.files.get('status'),
  player: filteredState.container.get('player'),
  selectedFilename: filteredState.files.get('selectedFilename'),
  selectorsMapping: filteredState.job.get('selectorsMapping'),
  galleryFullScreen: filteredState.container.get('galleryFullScreen'),
  logsViewerState: filteredState.container.get('logsViewerState'),
  screenshotBundleUrl,
}));

const PuppeteerContainerSelector = createSelector([
  job,
  jobSelectors.selectTestCaseScreenshots,
  jobSelectors.selectTestCases,
  jobSelectors.selectScreenshotIndex,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  location,
],
(filteredState, screenshots, testCases, screenshotIndex, user, routerLocation) => ({
  assets: filteredState.job.get('assets'),
  auth: parse(routerLocation.search, { ignoreQueryPrefix: true }).auth,
  build: filteredState.job.get('build'),
  columnsRatio: filteredState.container.get('ratio'),
  commandsLoadStatus: filteredState.job.get('commandsLoadStatus'),
  files: filteredState.files.get('files'),
  filteredTestResult: filterTestResult({
    filterState: filteredState.container.get('commandsFilter'),
    testResult: filteredState.job.get('commands'),
  }),
  isOpenSauceUser: !user.isAuthenticated(),
  isVideoReady: filteredState.job.get('assetListFetched'),
  logFetchStatus: filteredState.files.get('status'),
  job: filteredState.job.get('job'),
  jobHash: filteredState.rootCause.get('testHash'),
  pattern: filteredState.rootCause.get('pattern'),
  player: filteredState.container.get('player'),
  screenshotIndex,
  screenshots,
  selectedFilename: filteredState.files.get('selectedFilename'),
  showFailedTests: filteredState.container.get('commandsFilter').showFailedTests,
  testcaseCount: testCases.size,
  testCases,
  user,
  logsViewerState: filteredState.container.get('logsViewerState'),
}));

const selectRegressionOverview = createSelector([
  (store) => store.apps.performance.regression.get('performanceRegressions'),
],
(performanceRegressions) => (
  utils.getRegressionOverview(performanceRegressions)
));

const selectFilteredCommands = createSelector([
  (store) => store.apps.job.job.get('job'),
  (store) => store.apps.job.job.get('commands'),
  (store) => store.apps.job.container.get('commandsFilter'),
  (store) => store.apps.performance.jobPerformance.get('rawPerformanceData'),
  (store) => store.apps.performance.regression.get('performanceRegressions'),
  (store) => store.apps.job.rootCause.get('lines'),
],
(jobRecord, commands, filterState, performanceData, performanceRegressions, rootCauseLines) => (
  filterCommands({
    automationBackend: jobRecord.automation_backend,
    commands,
    filterState,
    performanceData,
    performanceRegressions,
    rootCauseLines,
  })
));

const selectAuth = createSelector(
  [
    (store) => store.router.location.search,
  ],
  (locationSearch) => (
    parse(locationSearch, { ignoreQueryPrefix: true }).auth
  ),
);

const WebdriverContainerSelector = createSelector([
  (store) => store.apps.job.container,
  (store) => store.apps.job.job,
  (store) => store.apps.job.rootCause,
  (store) => store.apps.job.files,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  selectAuth,
  performance,
  selectFilteredCommands,
  selectRegressionOverview,
  jobSelectors.selectActiveCommand,
  jobSelectors.selectScreenshotBundleUrl,
],
(
  containerSlice,
  jobSlice,
  rootCauseSlice,
  filesSlice,
  user,
  auth,
  performanceStore,
  filteredCommands,
  regressionOverview,
  activeCommand,
  screenshotBundleUrl,
) => ({
  activeCommand,
  auth,
  columnsRatio: containerSlice.get('ratio'),
  loading: containerSlice.get('loading'),
  player: containerSlice.get('player'),
  commandCount: jobSlice.get('commands')?.size || 0,

  filteredCommands,
  assets: jobSlice.get('assets'),
  commandsLoadStatus: jobSlice.get('commandsLoadStatus'),
  job: jobSlice.get('job'),
  selectorsMapping: jobSlice.get('selectorsMapping'),
  build: jobSlice.get('build'),
  isVideoReady: jobSlice.get('assetListFetched'),

  files: filesSlice.get('files'),
  selectedFilename: filesSlice.get('selectedFilename'),
  activeRightPanelTab: filesSlice.get('activeRightPanelTab'),
  activeLeftPanelTab: filesSlice.get('activeLeftPanelTab'),
  logFetchStatus: filesSlice.get('status'),
  user,
  isOpenSauceUser: !user.isAuthenticated(),
  isJobOwner: user.username === jobSlice.getIn(['job', 'owner']),
  regressionData: performanceStore.regression.get('performanceRegressions'),
  regressionOverview,
  loaderId: performanceStore.jobPerformance.get('loaderId'),
  hasRootCause: containerSlice.get('commandsFilter').hasRootCause,
  hasPerformance: containerSlice.get('commandsFilter').hasPerformance,
  annotationsName: rootCauseSlice.get('annotationsName'),
  jobHash: rootCauseSlice.get('testHash'),
  pattern: rootCauseSlice.get('pattern'),
  showFailedTests: containerSlice.get('commandsFilter').showFailedTests,
  galleryFullScreen: containerSlice.get('galleryFullScreen'),
  logsViewerState: containerSlice.get('logsViewerState'),
  screenshotBundleUrl,
}));

const WebdriverLivePreviewContainerSelector = createSelector(
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  (filteredState, user) => ({
    runningState: filteredState.job.get('runningState'),
    job: filteredState.job.get('job'),
    isFreeUser: user.isFreeUser(),
    isOpenSauceUser: !user.isAuthenticated(),
    logsViewerState: filteredState.container.get('logsViewerState'),
  }),
);

const PublicRestrictedContainerSelector = createSelector(
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  (filteredState, user) => ({
    build: filteredState.job.get('build'),
    columnsRatio: filteredState.container.get('ratio'),
    job: filteredState.job.get('job'),
    isOpenSauceUser: !user.isAuthenticated(),
    player: filteredState.container.get('player'),
    user,
    logsViewerState: filteredState.container.get('logsViewerState'),
  }),
);

const espressoContainerSelector = createSelector([
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  location,
  jobSelectors.selectScreenshotList,
  jobSelectors.selectScreenshotBundleUrl,
],
(filteredState, user, routerLocation, screenshots, screenshotBundleUrl) => ({
  assets: filteredState.job.get('assets'),
  auth: parse(routerLocation.search, { ignoreQueryPrefix: true }).auth,
  columnsRatio: filteredState.container.get('ratio'),
  commandCount: filteredState.job.get('commands')?.size || 0,
  commandsLoadStatus: filteredState.job.get('commandsLoadStatus'),
  files: filteredState.files.get('files'),
  filteredCommands: filterEspressoTests({
    filterState: filteredState.container.get('commandsFilter'),
    testClasses: filteredState.job.get('commands'),
  }),
  isJobOwner: user.username === filteredState.job.getIn(['job', 'owner']),
  isOpenSauceUser: !user.isAuthenticated(),
  isVideoReady: filteredState.job.get('assetListFetched'),
  job: filteredState.job.get('job'),
  logFetchStatus: filteredState.files.get('status'),
  player: filteredState.container.get('player'),
  screenshotBundleUrl,
  screenshots,
  selectedFilename: filteredState.files.get('selectedFilename'),
  activeRightPanelTab: filteredState.files.get('activeRightPanelTab'),
  activeLeftPanelTab: filteredState.files.get('activeLeftPanelTab'),
  showFailedTests: filteredState.container.get('commandsFilter').showFailedTests,
  user,
  logsViewerState: filteredState.container.get('logsViewerState'),
}));

const manualRdcContainerSelector = WebdriverContainerSelector;

const playerSelector = (state) => job(state).container.get('player');

const allCommandsSelector = createSelector(job, (jobState) => jobState.job.get('commands'));

// Respects commandsFilter
const filteredCommandsSelector = createSelector(job, performance, (
  jobState, performanceStore,
) => {
  const jobRecord = jobState.job.get('job');

  if (jobRecord.isJSFrameworks() || jobRecord.isPuppeteerReplay()) {
    return filterTestResult({
      filterState: jobState.container.get('commandsFilter'),
      testResult: jobState.job.get('commands'),
    });
  }

  return filterCommands({
    commands: jobState.job.get('commands'),
    filterState: jobState.container.get('commandsFilter'),
    performanceData: performanceStore.jobPerformance.get('rawPerformanceData'),
    performanceRegressions: performanceStore.regression.get('performanceRegressions'),
  });
});

// Respects commandsFilter
const commandSelector = (id) => (state) => filteredCommandsSelector(state)
  .find((command) => command.id === id);

const appiumContainerSelector = WebdriverContainerSelector;

const appiumLivePreviewContainerSelector = createSelector(
  job,
  (filteredState) => ({
    job: filteredState.job.get('job'),
    build: filteredState.job.get('build'),
    device: filteredState.rdcPreviewSession.get('device'),
    sessionState: filteredState.rdcPreviewSession.get('sessionState'),
    runningState: filteredState.job.get('runningState'),
  }),
);

const rawJobContainerSelector = createSelector(
  job,
  (filteredState) => ({ rawJob: filteredState.rawJob }),
);

const videoPlayerJobDataSelector = createSelector([job], (jobStore) => ({
  startTime: jobStore.job.getIn(['job', 'start_time']) * 1000,
}));

const currentTimeSelector = createSelector([
  filteredCommandsSelector,
  job,
  (store, options) => options,
],
(filteredCommands, jobStore, options = {}) => {
  const timeDiff = new Date(options.timestamp).getTime() - jobStore.job.getIn(['job', 'start_time']) * 1000;
  return {
    commands: filteredCommands,
    currentTime: timeDiff / 1000,
    player: jobStore.container.get('player'),
  };
});

const deviceVitalsSelector = createSelector([
  (store) => store.apps.job.job.get('testfairySdkData'),
  (store) => store.apps.job.job.get('job'),
  (store) => store.apps.job.container,
],
(testfairySdkData, jobRecord, containerSlice) => {
  const { session = {} } = testfairySdkData || {};
  return {
    ...session,
    player: containerSlice.get('player'),
    job: jobRecord,
  };
});

const crashLogsSelector = createSelector([
  (store) => store.apps.job.job.get('crashLogs'),
  (store) => store.apps.job.job.get('job'),
],
(crashLogs, jobRecord) => ({
  crashLogs: crashLogs?.crash && processCrashLogs({ crashLogs, job: jobRecord }),
}));

const deviceEventsSelector = createSelector([
  (store) => store.apps.job.job.get('deviceEvents'),
],
(deviceEvents) => ({
  deviceEvents,
  didRestrictDeviceSettingsAccess: deviceEvents ? deviceEvents['apps.killed']?.length > 0 : false,
}));

export default containerSelector;
export {
  WebdriverContainerSelector,
  WebdriverLivePreviewContainerSelector,
  PuppeteerContainerSelector,
  PuppeteerReplayContainerSelector,
  PublicRestrictedContainerSelector,
  allCommandsSelector,
  appiumContainerSelector,
  manualRdcContainerSelector,
  appiumLivePreviewContainerSelector,
  commandSelector,
  containerSelector,
  espressoContainerSelector,
  filteredCommandsSelector,
  job,
  playerSelector,
  rawJobContainerSelector,
  location,
  videoPlayerJobDataSelector,
  currentTimeSelector,
  deviceVitalsSelector,
  crashLogsSelector,
  deviceEventsSelector,
};
